/**
 * useShellApproach - the group approach on this screen.
 *
 * The roles (who leads, who helps, who is a burden) are on the ladder's
 * record and anyone may read them. HOW WELL the approach was made is kept
 * on the GM's desk (`useGmDeskValue`) and is never sent to a player.
 *
 * The director's verbs:
 * - `setLeader`, `setRole` - who does what. Any change makes the approach
 *   untested again, and what it was made with is forgotten.
 * - `test()` - the leader rolls Silent Move for everybody, in secret.
 * - `look()` - the sharpest of the opposition rolls Awareness against it, in
 *   secret, and the answer becomes a proposal for the ladder (through
 *   `quiet.propose`), like any other quiet move.
 *
 * @param {object} s - the shell scope: stealth, roster, sessionId, isGm,
 *   notch, unnaturalDosMode, assistBonus, postSecret, quiet.
 * @returns {{ approach, company, degrees: number|null, helperBonus: number, canDirect: boolean, setLeader, setRole, test, look }}
 */
import { useCallback, useMemo, useRef } from 'react';
import * as multiplayer from '../multiplayer';
import { resolveTestCall } from '../engine/narrative/callForTest';
import {
  approachCall,
  degreesOf,
  lookCall,
  lookOutcome,
  withLeader,
  withRole,
  withTested,
} from '../engine/stealth/approach';
import { proposeFor } from '../engine/stealth/proposals';
import useGmDeskValue from './useGmDeskValue';

/** The desk's name for how well the approach was made. */
const DESK_ID = 'approach';

/** The degrees held on the desk, as a number or null. */
const degreesHeld = (value) => (typeof value?.degrees === 'number' ? value.degrees : null);

/** Who may have a part in the approach: everyone on the roster the party is not hiding from. */
function companyOf(roster, opposition) {
  const hidingFrom = new Set(opposition.map((c) => c.id));
  return roster.filter((c) => !hidingFrom.has(c.id));
}

export default function useShellApproach(s) {
  const { stealth, roster } = s;
  const { approach } = stealth.state;
  const seat = useMemo(() => ({ sessionId: s.sessionId, isGm: !!s.isGm, mp: multiplayer }), [s.sessionId, s.isGm]);
  const [held, setHeld] = useGmDeskValue(seat, DESK_ID);
  const degrees = approach.tested ? degreesHeld(held) : null;
  const company = useMemo(() => companyOf(roster, stealth.opposition), [roster, stealth.opposition]);

  const live = useRef({ s, degrees, setHeld });
  live.current = { s, degrees, setHeld };

  /** Change who does what: the approach as it stood is over. */
  const change = useCallback((next) => {
    const now = live.current;
    now.s.stealth.apply((st) => ({ ...st, approach: next(st.approach) }));
    now.setHeld(null);
  }, []);
  const setLeader = useCallback((id) => change((a) => withLeader(a, id)), [change]);
  const setRole = useCallback((id, role) => change((a) => withRole(a, id, role)), [change]);

  const test = useCallback(() => {
    const { s: now, setHeld: keep } = live.current;
    const call = approachCall(now.stealth.state.approach, now.notch?.mine ?? 0, now.assistBonus);
    if (!call || !now.stealth.canDirect) return;
    const [result] = resolveTestCall(call, now.roster, { unnaturalDosMode: now.unnaturalDosMode });
    const made = degreesOf(result);
    if (result) now.postSecret(`The approach - ${result.entry.summary}`);
    if (made === null) return;
    keep({ degrees: made });
    now.stealth.apply((st) => ({ ...st, approach: withTested(st.approach) }));
  }, []);

  const look = useCallback(() => {
    const { s: now, degrees: made } = live.current;
    const watcher = now.stealth.sharpest;
    if (made === null || !watcher || !now.stealth.canDirect) return;
    const [result] = resolveTestCall(lookCall(watcher.id, now.notch?.theirs ?? 0), now.roster, {
      unnaturalDosMode: now.unnaturalDosMode,
    });
    if (result) now.postSecret(`${watcher.name} looks - ${result.entry.summary}`);
    const outcome = lookOutcome(made, result);
    if (!outcome) return;
    now.quiet.propose({ title: `${watcher.name} looks`, ...proposeFor('approach', outcome, now.stealth.state) });
  }, []);

  const helperBonus = s.assistBonus ?? 10;
  return useMemo(
    () => ({ approach, company, degrees, helperBonus, canDirect: stealth.canDirect, setLeader, setRole, test, look }),
    [approach, company, degrees, helperBonus, stealth.canDirect, setLeader, setRole, test, look],
  );
}
